import { View, Text, Link, StyleSheet, Font } from "@react-pdf/renderer";

import KarmaRegular from "assets/fonts/Karma-Regular.ttf";
import KarmaSemiBold from "assets/fonts/Karma-SemiBold.ttf";
import KarmaBold from "assets/fonts/Karma-Bold.ttf";
import arialHindi from "assets/fonts/arial-unicode-ms.ttf";
import AnekTelugu from "assets/fonts/AnekTelugu.ttf";

Font.register({
  family: "Karma",
  fonts: [
    { src: KarmaRegular, fontWeight: 400 },
    { src: KarmaSemiBold, fontWeight: 700 },
    { src: KarmaBold, fontWeight: 700 },
  ],
});

Font.register({
  family: "arialHindi",
  fonts: [{ src: arialHindi, fontWeight: 400 }],
});

export function getFontFamilyForText(text) {
  if (/[ఀ-౿]/.test(text)) return "AnekTelugu"; // Telugu range
  if (/[ऀ-ॿ]/.test(text)) return "arialHindi"; // Hindi range
  return "arialHindi";
}

Font.register({
  family: "AnekTelugu",
  fonts: [{ src: AnekTelugu, fontWeight: 400 }],
});

const styles = StyleSheet.create({
  table: {
    display: "table",
    width: "100%",
    marginTop: 10,
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#ccc",
  },
  row: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#ccc",
    minHeight: 24,
  },
  headerRow: {
    flexDirection: "row",
    backgroundColor: "#eee",
    borderBottomWidth: 2,
    borderBottomColor: "#333",
    minHeight: 26,
    fontWeight: "bold",
    fontFamily: "Roboto",
    fontSize: '10px',
  },
  headerCell: {
    borderRightWidth: 1,
    borderRightColor: "#ccc",
    // fontSize: 9,
    fontWeight: "bold",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#eee",
    // width: "25%",
    textTransform: "uppercase",
  },
  cell: {
    borderRightWidth: 1,
    borderRightColor: "#ccc",
    padding: 4,
    fontSize: 9,
    justifyContent: "center",
    alignItems: "flex-start",
  },
  // headerCell: {
  //   borderRightWidth: 1,
  //   borderRightColor: "#ccc",
  //   padding: 6,
  //   fontSize: 10,
  //   fontWeight: "bold",
  //   justifyContent: "center",
  //   alignItems: "center",
  //   backgroundColor: "#eee",
  // },
  indexCell: {
    borderRightWidth: 1,
    borderRightColor: "#ccc",
    padding: 4,
    fontSize: 9,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#f8f9fa",
    fontWeight: "bold",
  },
  link: {
    color: "blue",
    textDecoration: "none",
  },
  contentCell: {
    flex: 1,
    padding: 4,
    fontSize: 9,
    justifyContent: "center",
    alignItems: "flex-start",
    borderRightWidth: 1,
    borderRightColor: "#ccc",
  },
});

export default function Appendix({
  pages = [],
  columns = 1,
  index = false,
  startIndex = 1,
  headerRows = [], 
}) {
  const INDEX_COLUMN_WIDTH = "8%";

  const getContentColumnWidth = () => {
    if (index) return `${92 / columns}%`;
    return `${100 / columns}%`;
  };

  const contentColumnWidth = getContentColumnWidth();

  const getColumnWidthBySpan = (span = 1) => {
    const base = parseFloat(contentColumnWidth);
    return `${base * span}%`;
  };

  const rows = (() => {
    const rows = [];
    let currentIndex = startIndex;

    for (let i = 0; i < pages.length; i += columns) {
      const rowData = [];

      if (index) {
        rowData.push({
          type: "index",
          value: currentIndex++,
          width: INDEX_COLUMN_WIDTH,
        });
      }

      for (let j = 0; j < columns; j++) {
        const item = pages[i + j];
        rowData.push({
          type: "content",
          item,
          width: contentColumnWidth,
          isEmpty: !item,
        });
      }

      rows.push(rowData);
    }
    return rows;
  })();

  return (
    <View style={styles.table}>
      {/* ===== MULTI HEADER ROWS ===== */}
      {headerRows.map((row, rowIndex) => (
        <View key={`header-row-${rowIndex}`} style={styles.headerRow} fixed>
          {row.cells.map((cell, cellIndex) => {
            const span = cell.colSpan ?? 1;

            let width;
            if (index && cellIndex === 0 && span === 1) {
              width = INDEX_COLUMN_WIDTH;
            } else {
              width = getColumnWidthBySpan(span);
            }

            return (
              <View
                key={`header-cell-${rowIndex}-${cellIndex}`}
                style={[
                  styles.headerCell,
                  {
                    width,
                    textAlign: cell.align ?? "center",
                  },
                ]}
              >
                <Text>{cell.text}</Text>
              </View>
            );
          })}
        </View>
      ))}

      {/* ===== TABLE BODY ===== */}
      {rows.map((row, rowIndex) => (
        <View style={styles.row} key={`row-${rowIndex}`}>
          {row.map((cell, cellIndex) => {
            if (cell.type === "index") {
              return (
                <View
                  key={`index-${cellIndex}`}
                  style={[styles.indexCell, { width: cell.width }]}
                >
                  <Text>{cell.value}</Text>
                </View>
              );
            }

            if (cell.type === "content") {
              if (cell.isEmpty) {
                return (
                  <View
                    key={`empty-${cellIndex}`}
                    style={[styles.contentCell, { width: cell.width }]}
                  />
                );
              }

              const title =
                cell.item?.title ?? cell.item?.account ?? cell.item?.name ?? "";
              const link = cell.item?.link;

              return (
                <View
                  key={`content-${cellIndex}`}
                  style={[
                    styles.contentCell,
                    {
                      width: cell.width,
                      fontFamily: getFontFamilyForText(title),
                    },
                  ]}
                >
                  {link ? (
                    <Link src={link} style={styles.link}>
                      <Text>{title}</Text>
                    </Link>
                  ) : (
                    <Text>{title}</Text>
                  )}
                </View>
              );
            }

            return null;
          })}
        </View>
      ))}
    </View>
  );
}
